import {Component, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild,} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import { BnFileUploaderComponent } from "@bndes/bndes-ux4/bn-file-uploader";
import {Observable, Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {MensagemService} from 'src/app/features/mensagem.service';
import {
  AcompanhamentoDocumentoDepr,
  PeriodoAcompanhamentoDocumentoDepr,
  periodosAcompanhamentoDocumentoDepr,
  TipoAcompanhamentoDocumentoDepr,
  tiposAcompanhamentoDocumentoDepr,
} from 'src/app/model/acompanhamento-documento-depr.model';
import {DataSistemaService} from 'src/app/shared/services/data-sistema.service';
import {AcompanhamentoDeprService} from '../../acompanhamento-depr.service';
import {EntradaAcompanhamentoDocumentoDeprDTO} from 'src/app/model/entrada-acompanhamento-documento-depr';
import {FileUtilsService} from '../../../../../shared/file-utils.service';
import {Router} from '@angular/router';
import { BnLoadingService } from "@bndes/bndes-ux4/bndes-angular-utils";

@Component({
  selector: "app-aba-documento",
  templateUrl: "./aba-documento.component.html",
  styleUrls: ["./aba-documento.component.scss"],
  standalone: false,
})
export class AbaDocumentoComponent implements OnInit, OnDestroy, OnChanges {
  private readonly destroy$ = new Subject();
  tiposDocs = tiposAcompanhamentoDocumentoDepr;
  periodos = periodosAcompanhamentoDocumentoDepr;
  anosCompetencia: number[] = [];
  periodoEhObrigatorio: boolean = false;
  exibePeriodo: boolean = false; // No cadastro nunca exibe; no detalhe exibe apenas se houver período salvo.
  formGroup: FormGroup;
  private anexo: File;
  @Input("clickEnviar") clickEnviar = new Observable();
  @Input("acompanhamentoDepr") acompanhamentoDepr: AcompanhamentoDocumentoDepr;
  @Input("acompanhamentoDetalhe") acompanhamentoDetalhe: boolean;
  @Output("updateStatusDisabledBtnEnviar") updateStatusDisabledBtnEnviar =
    new Subject();
  @ViewChild(BnFileUploaderComponent) bnFileUploader: BnFileUploaderComponent;

  constructor(
    formBuilder: FormBuilder,
    private dataSistemaService: DataSistemaService,
    private mensagemService: MensagemService,
    private acompanhamentoDeprService: AcompanhamentoDeprService,
    private fileUtils: FileUtilsService,
    private router: Router,
    private loadingService: BnLoadingService
  ) {
    this.formGroup = formBuilder.group({
      tipo: [null, Validators.required],
      anoCompetencia: [null, Validators.required],
      periodo: null,
      nomeAnexo: null,
      motivoEstado: null,
      identificadorDocumento: null
    });
    this.formGroup.controls["periodo"].disable();
    this.formGroup.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        const formInvalid = status == "INVALID";
        this.emitUpdateStatusDisabledBtnEnviar(formInvalid);
      });
  }

  emitUpdateStatusDisabledBtnEnviar(formInvalid?: boolean) {
    if (formInvalid == null) {
      formInvalid = this.formGroup.invalid;
    }
    const fileNotSelected = !this.temArquivoParaEnviar;
    this.updateStatusDisabledBtnEnviar.next(formInvalid || fileNotSelected);
  }

  novoArquivoPendenteUpload(event) {
    this.anexo = event.arquivo;
    this.emitUpdateStatusDisabledBtnEnviar();
  }

  arquivoPendenteUploadRemovido(event) {
    this.emitUpdateStatusDisabledBtnEnviar();
  }

  exportarAnexo() {
    this.acompanhamentoDeprService.getArquivo(this.acompanhamentoDepr.id).subscribe((conteudo) => {
      this.fileUtils.exportarArquivo(this.acompanhamentoDepr.nomeAnexo, conteudo, FileUtilsService.APPLICATION_OCTEC);
    });
  }

  ngOnInit(): void {
    this.clickEnviar
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.enviar());
    this.carregaDataSistema().then(r => r);
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['acompanhamentoDepr'] && changes['acompanhamentoDepr'].currentValue) {
      this.formGroup.controls["periodo"].disable();
      this.carregaVizualizacaoComDados();
    }
  }

  carregaVizualizacaoComDados(){
    if (this.acompanhamentoDetalhe) {
      this.encontraTipoDocumento();
      this.encontraPeriodo();
      // No detalhe exibe o Período apenas se o registro possuir período salvo.
      // Registros antigos (semestrais) têm período => exibe; novos sem período => não exibe.
      this.exibePeriodo = this.acompanhamentoDepr.periodo != null;
      this.formGroup.patchValue(this.acompanhamentoDepr);
      this.formGroup.controls["periodo"].disable();
      this.formGroup.controls["nomeAnexo"].disable();
      this.formGroup.controls["anoCompetencia"].disable();
      this.formGroup.controls["tipo"].disable();
      this.formGroup.controls["motivoEstado"].disable();
      this.formGroup.controls["identificadorDocumento"].disable();
    }
  }

  encontraTipoDocumento() {
    this.tiposDocs.find(tipo => {
      if (tipo.id == this.acompanhamentoDepr.tipo) {
        this.acompanhamentoDepr.tipo = tipo.valor;
      }
    });
  }

  encontraPeriodo() {
    this.periodos.find(periodo => {
      if (periodo.id == this.acompanhamentoDepr.periodo) {
        this.acompanhamentoDepr.periodo = periodo.valor;
      }
    });
  }

  compararPorId(item1: string, item2: string): boolean {
    return (item1 && item2) ? (item1 === item2) : (!item1 && !item2);
  }

  async carregaDataSistema() {
    const dataCorrente = await this.dataSistemaService
      .getDataCorrente()
      .toPromise();
    this.preencheAnosAcompetencia(dataCorrente);
  }

  preencheAnosAcompetencia(dataCorrente: Date) {
    this.anosCompetencia = [];
    const anoInicial = 2022;
    for (let ano = anoInicial; ano <= dataCorrente.getFullYear(); ano++) {
      this.anosCompetencia.push(ano);
    }
  }

  get temArquivoParaEnviar() {
    return (
      this.bnFileUploader &&
      this.bnFileUploader.listaPendente &&
      this.bnFileUploader.listaPendente.length > 0
    );
  }

  async enviar() {
    this.loadingService.bloqueiaTela();
    if (!this.temArquivoParaEnviar) {
      this.mensagemService.adicionarMensagemErro(
        "O arquivo não foi adicionado."
      );
      return;
    }

    const entradaDto = this.formGroup
      .value as EntradaAcompanhamentoDocumentoDeprDTO;
    try {
      const acompDepr = await this.acompanhamentoDeprService
        .salvar(entradaDto)
        .toPromise();
      if (acompDepr && acompDepr.id) {
        await this.acompanhamentoDeprService.enviaDocumento(acompDepr.id, this.anexo).toPromise();
        this.mensagemService.adicionarMensagemSucesso("Documento enviado para avaliação do DEPR.")
        this.loadingService.liberaTela();
        await this.router.navigate(["/acompanhamento-documento-depr/pesquisa"]);
      } else {
        this.mensagemService.adicionarMensagemErro(
          "Houve algum erro ao salvar"
        );
        this.loadingService.liberaTela();
      }
    } catch (err) {
      this.mensagemService.adicionarMensagemErro(
        "Erro ao salvar acompanhamento DEPR"
      );
      this.loadingService.liberaTela();
    }
  }

  exibeMotivoRejeicao() {
    return Number(this.acompanhamentoDepr?.estado?.id) === PeriodoAcompanhamentoDocumentoDepr.REJEITADO.valueOf();
  }

  exibeMotivoValidacao() {
    return Number(this.acompanhamentoDepr?.estado?.id) === PeriodoAcompanhamentoDocumentoDepr.VALIDADO.valueOf();
  }
}
